import { FLAGS } from '@iheartjane/feature-flags';
import { Loading } from '@iheartjane/reefer';
import type { Nullable } from '@iheartjane/types';
import type {
  ActionFunctionArgs,
  LinksFunction,
  LoaderFunctionArgs,
  MetaFunction,
} from 'react-router';
import { useLoaderData } from 'react-router';

import {
  clearCartAction,
  refreshCartAction,
} from '@jane/bloom/actions/cart.server';
import { useIsNavigatingTo } from '@jane/bloom/hooks/navigation/useIsNavigatingTo';
import { globalSubloader } from '@jane/bloom/loaders/globalSubloader.server';
import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { description } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { noindex } from '@jane/bloom/resources/meta/noindex';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { preconnectLinks } from '@jane/bloom/resources/meta/preconnectLinks';
import { title } from '@jane/bloom/resources/meta/title';
import { getCartInfo } from '@jane/bloom/sessions/cart.server';
import { getUserIdentity } from '@jane/bloom/sessions/userIdentity.server';
import { CartAction } from '@jane/bloom/types/schemas/bloom/cart';
import type { RouteHandle } from '@jane/bloom/types/schemas/bloom/routeHandle';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';
import type { CheckoutViewModel } from '@jane/bloom/viewModels/checkout.server';
import { buildCheckoutViewModel } from '@jane/bloom/viewModels/checkout.server';

import { LoginModal } from './components/loginModal';
import stylesLink from './styles.css?url';
import { useInitializeCart } from './useInitializeCart';

export const handle: RouteHandle = {
  hydrate: true,
  scripts: () => [
    /** @see https://developers.google.com/identity/gsi/web/guides/client-library */
    {
      async: true,
      defer: true,
      src: 'https://accounts.google.com/gsi/client',
    },
  ],
};

export const links: LinksFunction = () => [
  { href: stylesLink, rel: 'stylesheet' },
];

export const meta: MetaFunction<typeof loader> = (args) => {
  const {
    appConfig: { headlessEmbedLink },
    menuUrl,
    operator: { name },
    pinnedStore,
  } = getRootLoaderData(args);
  return [
    title('Bag', pinnedStore?.name || name),
    description(),
    canonicalTag(args.location, menuUrl),
    noindex(),
    ...preconnectLinks([headlessEmbedLink]),
    ...parentMeta(args),
  ];
};

type CheckoutLoaderData = {
  checkoutData: CheckoutViewModel;
  googleSsoClientId?: Nullable<string>;
  isBloomMessagingForMigratedUsersEnabled: boolean;
};

export async function loader({ request }: LoaderFunctionArgs) {
  const cookie = request.headers.get('Cookie');

  const url = new URL(request.url);
  const searchParams = new URLSearchParams(url.searchParams);

  const menu = await ensureBloomMenu(request);
  const { shoppingPreferences } = await globalSubloader(request, menu);
  const cart = await getCartInfo(cookie, menu.cookie_prefix);
  const userIdentity = await getUserIdentity(request, menu.cookie_prefix);

  const checkoutData = buildCheckoutViewModel({
    cart,
    deliveryAddress: {
      city: shoppingPreferences.city ?? '',
      country_code: shoppingPreferences.countryCode ?? 'US',
      lat: shoppingPreferences.latitude ?? 0,
      lng: shoppingPreferences.longitude ?? 0, // Passing `long` and `lng`
      long: shoppingPreferences.longitude ?? 0, // so that the API can use either
      state_code: shoppingPreferences.state ?? '',
      street: shoppingPreferences.streetAddress,
      street2: shoppingPreferences.streetAddress2,
      zipcode: shoppingPreferences.zipcode,
    },
    jwt: userIdentity?.jwtToken,
    menu,
    skipBag: Boolean(searchParams.get('skipBag')),
    storeId: shoppingPreferences.storeId,
    user: userIdentity?.user,
  });

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [FLAGS.bloomMessagingForMigratedUsers],
    request,
  });

  const isBloomMessagingForMigratedUsersEnabled =
    featureFlags[FLAGS.bloomMessagingForMigratedUsers];

  const result: CheckoutLoaderData = {
    checkoutData,
    googleSsoClientId: menu.google_sso_client_id,
    isBloomMessagingForMigratedUsersEnabled,
  };
  return result;
}

export async function action(args: ActionFunctionArgs) {
  const intent = (await args.request.clone().formData()).get('intent');

  switch (intent) {
    case CartAction.ClearCart: {
      return await clearCartAction(args);
    }
    case CartAction.RefreshCart: {
      return await refreshCartAction(args);
    }
    default: {
      throw new ReferenceError(`Action "${intent}" not found`);
    }
  }
}

export default function CheckoutRoute() {
  const {
    checkoutData,
    googleSsoClientId,
    isBloomMessagingForMigratedUsersEnabled,
  } = useLoaderData<typeof loader>();
  const { setShowAuthModal, showAuthModal, skipBagAfterAuth } =
    useInitializeCart({
      checkoutData,
    });

  if (useIsNavigatingTo(/.*\/checkout\/confirmation\/.*/)) {
    return <Loading color="black" size="lg" />;
  }

  return (
    <LoginModal
      googleSsoClientId={googleSsoClientId}
      isBloomMessagingForMigratedUsersEnabled={
        !!isBloomMessagingForMigratedUsersEnabled
      }
      setShowAuthModal={setShowAuthModal}
      showAuthModal={showAuthModal}
      skipBagAfterAuth={skipBagAfterAuth}
    />
  );
}
